import {
  FeedbackHandler,
  IAddOpponentToCallParams,
  IEventListItem,
} from '@gem/call/CallController/CallController.types';
import { ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import { Call } from '@gem/call/CallStore/Call';
import { initCallListener } from '@gem/call/Model/callModel';
import {
  CallMemberStatus,
  CallStatus,
  DeviceInfoPlatform,
  ICallMember,
  ICallMemberChanged,
  ICallMemberForceFinished,
  ICallMemberForceMuted,
  ICallMemberWasRemoved,
  ICallRecordingStarted,
  ICallUIFeatures,
  IGroupCallChanged,
  IMemberRoleChanged,
  IStatusCallChanged,
  IWaitingRoomChanged,
  IWaitingRoomMember,
  ParticipantsControlType,
} from '@gem/call/Model/callModel.types';
import { StatusCallFinishReason } from '@gem/call/Model/callModel.types';
import {
  CallMemberRole,
  ICallCustomPermissions,
  ICallPermissionsChanged,
  ICallUserPermissionRequestCreated,
  ICallUserPermissionsChanged,
  ICallUserPermissionsRequestChanged,
  PermissionsRequestStatuses,
  PermissionsTypes,
} from '@gem/call/Model/callPermissions/callPermissionsModel.types';
import { CallEventTypes, ClientRole, PERMISSIONS_REQUEST_TYPES } from '@gem/call/constants';
import { t } from '@gem/modules/i18n';
import { logger } from '@gem/modules/logger';
import { popupsTasks } from '@gem/popups';

const SSE_RECONNECT_INTERVAL = 3 * 1000;

interface ICallStateHandlerServiceHandlers {
  feedbackHandler?: FeedbackHandler;
  muteAudio(): void;
  muteBroadcasting(muteAudio: boolean, muteVideo: boolean, stopScreenSharing: boolean): void;
  addOpponentToCall(params: IAddOpponentToCallParams): void;
  checkUserLeftAndDelete(uid: ProviderUid): void;
  dispose(
    isInternalFinish?: boolean,
    justILeaveCall?: boolean,
    statusCallFinishReason?: StatusCallFinishReason
  ): Promise<void>;
  startBusy(): void;
  clearCallingTimer(): void;
  getCallingTimer(): ReturnType<typeof setInterval> | null;
  addEvent(event: IEventListItem): void;
  getCallMembers(channelId: string): Promise<void>;
  getCallMembersByIds(channelId: string, ids: string[]): Promise<void>;
  updateViewState(hideMembers: boolean): void;
}

interface ICallStateHandlerServiceParams {
  channelId: string;
  handlers: ICallStateHandlerServiceHandlers;
  call: Call;
}

export class CallStateHandlerService {
  disposeListener: (() => void) | null = null;
  channelId: string;
  handlers: ICallStateHandlerServiceHandlers;
  call: Call;

  constructor(params: ICallStateHandlerServiceParams) {
    this.call = params.call;
    this.channelId = params.channelId;
    this.handlers = params.handlers;
  }

  onActiveCallData(activeCall: IGroupCallChanged): void {
    const status = activeCall.status;

    if (status === CallStatus.Finished) {
      this.handlers.feedbackHandler && this.handlers.feedbackHandler(this.channelId);
      this.handlers.dispose();
    }
  }

  onMemberForceMuted(data: ICallMemberForceMuted): void {
    this.handlers.addEvent({
      initiatorId: data.initiatorId,
      userId: data.targetMemberId,
      type: CallEventTypes.ForceMute,
    });

    if (data.deviceId === this.call.meStore.deviceId) {
      this.handlers.muteAudio();
    }
  }

  onMemberForceFinished(data: ICallMemberForceFinished): void {
    if (
      data.deviceId === this.call.meStore.deviceId &&
      (!data.platforms || data.platforms.includes(DeviceInfoPlatform.Web))
    ) {
      this.handlers.feedbackHandler && this.handlers.feedbackHandler(this.channelId);
      this.handlers.dispose();
    } else {
      this.handlers.addEvent({
        initiatorId: data.initiatorId,
        userId: data.targetMemberId,
        type: CallEventTypes.ForceFinish,
      });
      const member = this.call.members.getMemberById(data.targetMemberId);
      if (!member) {
        return;
      }

      const memberDevice = data.deviceId ? member.getDevice(data.deviceId) : null;

      if (!memberDevice || !memberDevice.uid || !memberDevice.deviceId) {
        return;
      }

      const opponent = this.call.opponents.getOpponent(memberDevice.uid);

      if (opponent) {
        this.call.opponents.deleteOpponent(opponent?.uid);
      }

      memberDevice.setState(CallMemberStatus.Left);
      member.deleteDevice(memberDevice.deviceId);
    }
  }

  onMemberWasRemoved(data: ICallMemberWasRemoved): void {
    const member = this.call.members.getMemberById(data.memberId);
    if (!member) {
      return;
    }

    if (member.isMe && this.call.isUnauthorizedCall) {
      this.handlers.dispose();

      return;
    }

    this.handlers.addEvent({
      userId: data.memberId,
      initiatorId: data.initiatorId,
      type: CallEventTypes.Removed,
    });

    member.devices.forEach((memberDevice) => {
      if (memberDevice.shareScreenUid) {
        this.call.opponents.deleteOpponent(memberDevice.shareScreenUid);
      }
      if (memberDevice.uid) {
        this.call.opponents.deleteOpponent(memberDevice.uid);
      }
    });

    this.call.members.deleteMember(member.userId);
  }

  onMemberWasAdded(data: ICallMember): void {
    this.call.members.setMembers([data]);
    if (this.call.uiFeatures.participantsControlType !== ParticipantsControlType.TotalNumber) {
      this.handlers.addEvent({ userId: data.userId, type: CallEventTypes.Added });
    }
  }

  async onUserData(data: ICallMemberChanged): Promise<void> {
    const { memberId, status, deviceId } = data;
    let member = this.call.members.getMemberById(memberId);
    let memberDevice = member?.getDevice(deviceId);
    const isDevicesPreviouslyOnCall = !!member && member.devicesOnCallList.length > 0;
    const wasDeviceOnCallBefore = !!memberDevice?.isOnCallByProvider;

    if (!member || !memberDevice) {
      await this.handlers.getCallMembers(this.channelId);
    }

    member = this.call.members.getMemberById(memberId);
    memberDevice = member?.getDevice(deviceId);

    switch (status) {
      case CallMemberStatus.OnCall: {
        if (!member || !memberDevice) {
          break;
        }

        if (!isDevicesPreviouslyOnCall && !wasDeviceOnCallBefore && !member.isMe) {
          this.handlers.addEvent({
            userId: member.userId,
            type: CallEventTypes.Joined,
          });
        }

        if (
          !memberDevice.isMyDevice &&
          memberDevice.uid &&
          !this.call.opponents.getOpponent(memberDevice.uid)
        ) {
          this.handlers.addOpponentToCall({
            uid: memberDevice.uid,
            hasVideo: false,
            hasAudio: false,
            opponentRole: ClientRole.Audience,
          });
        }

        memberDevice.setState(CallMemberStatus.OnCall);

        if (!this.handlers.getCallingTimer() || member.isMe) {
          break;
        }

        this.handlers.clearCallingTimer();

        if (this.call.startTs) {
          break;
        }

        this.call.setStartTs(Number(new Date()));

        break;
      }
      case CallMemberStatus.Declined: {
        const device = memberDevice || member?.defaultDevice;
        if (device && member) {
          device.setState(CallMemberStatus.Declined);
          if (
            this.call.uiFeatures.participantsControlType === ParticipantsControlType.Disabled &&
            !member.isMe &&
            device.state !== CallMemberStatus.OnCall
          ) {
            this.handlers.startBusy();
          }
        }
        break;
      }
      case CallMemberStatus.Left: {
        if (!member || !memberDevice) {
          break;
        }

        memberDevice.setState(CallMemberStatus.Left);

        if (memberDevice.uid) {
          this.handlers.checkUserLeftAndDelete(memberDevice.uid);
        }

        if (
          this.call.uiFeatures.participantsControlType === ParticipantsControlType.Disabled &&
          !member.isMe
        ) {
          this.handlers.feedbackHandler && this.handlers.feedbackHandler(this.channelId);
          this.handlers.dispose();
        }

        if (this.call.isUnauthorizedCall && member.isMe) {
          this.handlers.dispose(true, true);
          return;
        }

        if (memberDevice.isMyDevice) {
          this.handlers.dispose();
        }

        break;
      }
    }
  }

  async onRecordingStarted(data: ICallRecordingStarted): Promise<void> {
    const { recordingId, recorderId } = data;

    const recorder = this.call.members.getMemberById(recorderId);
    if (recorder) {
      const snackbarText = t('calls_recording_started_snackbar', { 0: recorder.displayName });

      popupsTasks.showToast(snackbarText, { durationMs: 5000 });
    }

    this.call.startRecording(recordingId);
  }

  async onRecordingStopped(): Promise<void> {
    const snackbarText = t('calls_recording_saved_to');

    popupsTasks.showToast(snackbarText, { durationMs: 5000 });

    this.call.stopRecording();
  }

  onPermissionsData(data: ICallPermissionsChanged): void {
    const changedPermissionType = Object.values(PermissionsTypes).find(
      (type) => !!data.changedPermissions[type]
    );

    if (!changedPermissionType) {
      return;
    }

    this.call.permissions.setPermissions(data);

    if (this.call.isMeInitiatorOwnerOrAdmin) {
      this.handlers.addEvent({
        type: CallEventTypes.PermissionsGlobalChanged,
        permissionType: changedPermissionType,
        permissionValue: data.default[changedPermissionType],
      });
    } else {
      this.handlers.addEvent({
        type: CallEventTypes.PermissionsGlobalMemberChanged,
        permissionType: changedPermissionType,
        permissionValue: data.changedPermissions[changedPermissionType],
      });
    }
  }

  setCustomMemberPermissions(data: ICallCustomPermissions): void {
    const customPermissions = this.call.permissions.customPermissions.get(data.userId);

    const currentPermissions = customPermissions ? { ...customPermissions } : {};

    Object.values(PermissionsTypes).forEach((key) => {
      if (data.personalizedCallPermissions[key]) {
        currentPermissions[key] = data.personalizedCallPermissions[key];
      }
    });

    this.call.permissions.setCustomUserPermissions(data.userId, currentPermissions);
  }

  setPersonalizedMemberPermissions(data: ICallCustomPermissions): any {
    const permissions = this.call.permissions.personalizedPermissions;
    const changedPermissions: any = {};

    Object.values(PermissionsTypes).forEach((key) => {
      if (data.personalizedCallPermissions[key]) {
        permissions[key] = data.personalizedCallPermissions[key];
        changedPermissions[key] = data.personalizedCallPermissions[key];
      }
    });

    this.call.permissions.setPersonalizedPermissions(permissions);

    return changedPermissions;
  }

  onMemberPermissionsData(data: ICallUserPermissionsChanged): void {
    if (this.call.isMeInitiatorOwnerOrAdmin) {
      if (data.purged) {
        this.call.permissions.deleteCustomUserPermissions(data.userId);
        return;
      }

      this.setCustomMemberPermissions(data);
    } else {
      if (data.purged) {
        this.call.permissions.setPersonalizedPermissions({});

        return;
      }

      const changedPermissions = this.setPersonalizedMemberPermissions(data);

      Object.keys(changedPermissions).forEach((item) => {
        this.handlers.addEvent({
          type: CallEventTypes.PermissionsMemberChanged,
          permissionType: item as PermissionsTypes,
          permissionValue: changedPermissions[item],
        });
      });
    }
  }

  onCreatePermissionsRequestData(data: ICallUserPermissionRequestCreated): void {
    const requestType = Object.keys(PERMISSIONS_REQUEST_TYPES).find((type: PermissionsTypes) => {
      return data.access === PERMISSIONS_REQUEST_TYPES[type];
    }) as PermissionsTypes;

    if (!requestType || !this.call.isMeInitiatorOwnerOrAdmin) {
      return;
    }

    this.call.permissions.addPermissionsRequest({
      userId: data.userId,
      requestType,
    });

    this.handlers.addEvent({
      type: CallEventTypes.PermissionsRequestCreated,
      userId: data.userId,
      requestType,
    });
  }

  onChangePermissionRequestData(data: ICallUserPermissionsRequestChanged): void {
    const requestTypes = Object.keys(PERMISSIONS_REQUEST_TYPES).reduce(
      (acc, type: PermissionsTypes) => {
        if (data.personalizedCallPermissions[type]) {
          acc.push(type);
        }
        return acc;
      },
      [] as PermissionsTypes[]
    );

    if (!requestTypes.length) {
      return;
    }

    if (this.call.isMeInitiatorOwnerOrAdmin) {
      requestTypes.forEach((type) => {
        this.call.permissions.deletePermissionsRequest({
          userId: data.userId,
          requestType: type,
        });
      });

      if (data.status === PermissionsRequestStatuses.Approved) {
        this.setCustomMemberPermissions(data);
      }
    } else {
      const changedPermissions = this.setPersonalizedMemberPermissions(data);

      Object.keys(changedPermissions).forEach((item) => {
        this.handlers.addEvent({
          type:
            data.status === PermissionsRequestStatuses.Approved
              ? CallEventTypes.PermissionsMemberChanged
              : CallEventTypes.PermissionsRequestDeclined,
          permissionType: item as PermissionsTypes,
          permissionValue: changedPermissions[item],
        });
      });
    }
  }

  beforeSetUiFeatures(uiFeatures: ICallUIFeatures): void {
    const { showControlMic, showControlCamera, showControlScreenSharing } = this.call.uiFeatures;
    const me = this.call.meStore;
    this.handlers.muteBroadcasting(
      showControlMic && !uiFeatures.showControlMic && !me.isAudioMuted,
      showControlCamera && !uiFeatures.showControlCamera && !me.isVideoMuted,
      showControlScreenSharing && !uiFeatures.showControlScreenSharing && me.isSharedScreen
    );
    this.handlers.updateViewState(
      !uiFeatures.isActiveControlParticipantListWeb ||
        uiFeatures.participantsControlType === ParticipantsControlType.Disabled
    );
  }

  onStatusCallChanged(data: IStatusCallChanged): void {
    const { status, waitingRoomEnabled, guestsAllowed, uiFeatures } = data;
    this.call.waitingRoom.setEnabled(waitingRoomEnabled);
    this.call.setGuestsAllowed(guestsAllowed);

    if (uiFeatures) {
      this.beforeSetUiFeatures(uiFeatures);
      this.call.uiFeatures.setUiFeatures(uiFeatures);
    }

    if (status !== CallStatus.Finished || !this.call) {
      return;
    }

    this.handlers.feedbackHandler && this.handlers.feedbackHandler(this.channelId);

    this.handlers.dispose(
      true,
      false,
      data.statusCallFinishReason && this.call.uiFeatures.showDurationLimitReachedBanner
        ? data.statusCallFinishReason
        : undefined
    );
  }

  onMemberRoleChanged(data: IMemberRoleChanged): void {
    const member = this.call.members.getMemberById(data.userId);
    if (!member) {
      this.handlers.getCallMembersByIds(this.channelId, [data.userId]);
      return;
    }
    member.setIsAdmin(data.role === CallMemberRole.Superuser);
    if (member.isMe) {
      this.call.setIsCallOwnerOrAdmin(data.role === CallMemberRole.Superuser);
    }
  }

  onWaitingRoomChanged(data: IWaitingRoomChanged): void {
    this.call.waitingRoom.setRequested(data.requested);
  }

  onWaitingRoomMemberChanged(data: IWaitingRoomMember): void {
    this.call.waitingRoom.changeMemberStatus(data);
  }

  connect(): void {
    if (this.channelId && !this.disposeListener) {
      this.disposeListener = initCallListener({
        channelId: this.channelId,
        isUnauthorizedCall: this.call.isUnauthorizedCall,
        onWaitingRoomChanged: this.onWaitingRoomChanged.bind(this),
        onWaitingRoomMemberChanged: this.onWaitingRoomMemberChanged.bind(this),
        onUserCallData: this.onUserData.bind(this),
        onActiveCallData: this.onActiveCallData.bind(this),
        onMemberWasAdded: this.onMemberWasAdded.bind(this),
        onMemberWasRemoved: this.onMemberWasRemoved.bind(this),
        onRecordingStarted: this.onRecordingStarted.bind(this),
        onRecordingStopped: this.onRecordingStopped.bind(this),
        onPermissionsData: this.onPermissionsData.bind(this),
        onMemberForceMuted: this.onMemberForceMuted.bind(this),
        onMemberForceFinished: this.onMemberForceFinished.bind(this),
        onMemberPermissionsData: this.onMemberPermissionsData.bind(this),
        onCreatePermissionsRequestData: this.onCreatePermissionsRequestData.bind(this),
        onChangePermissionRequestData: this.onChangePermissionRequestData.bind(this),
        onStatusCallChanged: this.onStatusCallChanged.bind(this),
        onMemberRoleChanged: this.onMemberRoleChanged.bind(this),
        onError: this.onError.bind(this),
      });
    }
  }

  dispose(): void {
    if (this.disposeListener) {
      this.disposeListener();
      this.disposeListener = null;
    }
  }

  async onError(err: { target: EventSource }): Promise<void> {
    try {
      this.dispose();
      // Check for error code in sse. I did not find another possibility in the specification
      // https://html.spec.whatwg.org/multipage/server-sent-events.html#the-eventsource-interface
      const controller = new AbortController();
      const { signal } = controller;
      const { status } = await fetch(err.target.url, { signal });
      controller.abort();
      if (status === 403) {
        this.handlers.feedbackHandler && this.handlers.feedbackHandler(this.channelId);
        this.handlers.dispose();
      } else {
        setTimeout(() => {
          this.connect();
        }, SSE_RECONNECT_INTERVAL);
      }
    } catch (e) {
      if (this.call) {
        setTimeout(() => {
          this.connect();
        }, SSE_RECONNECT_INTERVAL);
      }
      logger.get('SSE').error('CallStateHandler.onError', e);
    }
  }
}
